
import {
  LOCALE_FORMAT,
  DEFAULT_COUNTRY,
} from '@src/config/localisation/locale-config.constants'
import type {
  TAbsoluteLocale,
  TCountry,
  TCountryLanguage,
  TAbsoluteLocaleStandard,
  TCountryLanguageStandard,
  TCountryStandard,
  LocaleInfo,
  TRegionDetail,
} from '@src/config/localisation/locale-config.types'
import QuinceApiError from '@src/quince-site-common/src/domains/quince-api-error'
import AuthUtil from '@src/quince-site-common/src/utils/authUtil'
import { customerServiceClient, reportingServicesHttpClient } from '@src/quince-site-common/src/utils/HttpClient'

import { parseFormat } from './general.utils'
export const parseLocale = (locale: TAbsoluteLocale | TAbsoluteLocaleStandard): LocaleInfo => {
  return parseFormat(LOCALE_FORMAT, locale) as {
    country: TCountry
    language: TCountryLanguage<TCountry>
  }
}

// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
export function getCountryPrefixedCookieKey(key: string, locale?: TAbsoluteLocale | string): string {
  if (!locale) return key

  let country: string | undefined

  try {
    // Try parsing as TAbsoluteLocale first (client-side)
    const parsed = parseLocale(locale.toLowerCase() as TAbsoluteLocale)
    country = parsed.country.toLowerCase()
  } catch {
    // Fallback to manual extraction (server-side raw locale strings)
    country = locale.toLowerCase().split('-')[1]?.toLowerCase()
  }

  return country ? `${country}:${key}` : key
}

export const getStandardLocale = (locale: TAbsoluteLocale | TAbsoluteLocaleStandard): TAbsoluteLocaleStandard => {
  const parsedLocale = parseLocale(locale)
  return `${parsedLocale.language}-${parsedLocale.country.toUpperCase()}` as TAbsoluteLocaleStandard
}

export const getQuinceLocale = (locale: TAbsoluteLocale | TAbsoluteLocaleStandard) => {
  return locale.toLowerCase() as TAbsoluteLocale
}

export const getShortenedLocale = (locale: TAbsoluteLocale, regionDetails: TRegionDetail) => {
  const { country } = parseLocale(locale)
  if (locale === regionDetails.regions[DEFAULT_COUNTRY]?.defaultLanguageIsoCode) return ''
  if (locale === regionDetails.regions[country]?.defaultLanguageIsoCode) return country
  return locale
}

/**
 * Returns the value associated with the primaryKey if it exists in the object.
 * Otherwise, it returns the value associated with the fallbackKey.
 *
 * @param {T} obj - The object to search for the keys.
 * @param {K} primaryKey - The primary key to look for in the object.
 * @param {L} fallbackKey - The fallback key to use if the primary key is not found.
 * @returns {T[K]} - The value associated with the primaryKey or fallbackKey.
 */
export function getDisplayKey<T, K extends keyof T, L>(
  obj: T,
  primaryKey: K,
  fallback: L,
  isNewLocalisedFlow: boolean, // TODO:Sachin-Review - Rely on actual experiment - create new context
) {
  if (!isNewLocalisedFlow) return fallback
  if (typeof obj[primaryKey] === 'string' && obj[primaryKey] === '') return fallback
  return obj[primaryKey] ?? fallback
}

export const updateCommunicationPreference = async ({
  locale,
  regionDetails,
  abortController,
}: {
  locale: TAbsoluteLocaleStandard
  regionDetails: TRegionDetail
  abortController: AbortController | null
}) => {
  const country = parseLocale(locale).country
  const regionId = regionDetails.regions[country.toLowerCase() as TCountry]?.regionId // as regionDetails is in lowercase we need to convert the country to lowercase but comm API expects data in uppercase
  const loggedIn = AuthUtil.isLoggedIn()
  if (!regionId || !loggedIn) return

  try {
    const response = await customerServiceClient.post(
      '/settings',
      {
        preferredLanguage: locale,
        regionId,
      },
      {
        signal: abortController?.signal,
      },
    )
    return response
  } catch (error) {
    if (abortController?.signal.aborted) return
    console.error('Failed to update locale settings: ', error)
    throw new QuinceApiError({
      error: error as Error,
      message: 'Failed to update locale settings',
      apiName: 'settings',
    })
  }
}

export const getPreferredLanguage = async () => {
  try {
    const res = (await reportingServicesHttpClient.get('/customers/meta')) as {
      data: {
        preferredLanguage: TCountryLanguageStandard<TCountryStandard> | null
      }
    }
    return res.data.preferredLanguage
  } catch (error) {
    console.error('Failed to get preferred language: ', error)
    return null
  }
}

export const getAbsoluteLocale = (
  locale: TCountry | TAbsoluteLocale | '',
  regionDetails: TRegionDetail,
): TAbsoluteLocale => {
  if (locale === '') {
    return regionDetails.regions[DEFAULT_COUNTRY]?.defaultCurrencyIsoCode as TAbsoluteLocale
  }
  if (Object.hasOwn(regionDetails.regions, locale)) {
    return regionDetails.regions[locale as TCountry]?.defaultLanguageIsoCode as TAbsoluteLocale
  }
  return locale as TAbsoluteLocale
}

//make this dynamic from the region details
export const allUrlLocales = ['us', 'en-us', 'ca', 'fr-ca', 'en-ca'] // eslint-disable-line i18next/no-literal-string

export const removeLocaleFromUrl = (pathnameWithSearchParams: string) => {
  const potentialLocale = pathnameWithSearchParams.split('/')[2] ?? ''
  if (allUrlLocales.includes(potentialLocale)) {
    return pathnameWithSearchParams.split('/').slice(2).join('/')
  }
  return pathnameWithSearchParams
}



export const handleLocaleForUrl = (updatedLocale: string, pathnameWithSearchParams: string) => {
  const cleanedPath = removeLocaleFromUrl(pathnameWithSearchParams)
  const normalizedPath = cleanedPath.startsWith('/') ? cleanedPath : '/' + cleanedPath
  if (updatedLocale) {
    window.location.href = window.location.origin + '/' + updatedLocale + normalizedPath
  } else {
    window.location.href = window.location.origin + normalizedPath
  }
}

const REDIRECT_RULES: { from: string; to: string }[] = [{ from: '/account/orders', to: '/account/my-orders-returns' }]

// Maps certain paths to different destinations when switching locale.
export const transformPathAfterLocaleSwitch = (pathname: string): string => {
  try {
    if (!pathname || pathname === '/') return pathname
    for (const rule of REDIRECT_RULES) {
      if (pathname.includes(rule.from)) {
        return rule.to
      }
    }
    return pathname
  } catch {
    return pathname
  }
}
